import { useEffect, useMemo, useState } from 'react'

/**
 * The Nigerian city catalogue, as `GET /v1/discover/cities` serves it.
 *
 * One list for every city picker and every "near you": a city is a GeoNames
 * id ("gn:2352778" is Abuja) with a state, an LGA and a centre, so the same
 * kitchen is the same place on every screen and the distance between two
 * cities is arithmetic rather than a substring match.
 *
 * Distances here are city centre to city centre. That is the honest ceiling
 * of a city catalogue: it says Kubwa is about 15 km from Abuja, not how far a
 * customer's door is from a kitchen's. UI copy says "about" and rounds.
 *
 * Place data from GeoNames (geonames.org), licensed CC BY 4.0.
 */

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api'

export interface City {
  /** "gn:<geonameid>". */
  ref: string
  name: string
  state: string
  lga?: string
  /** GeoNames feature code: PPLC capital, PPLA state capital, PPLA2 LGA seat. */
  feature: string
  latitude: number
  longitude: number
}

/** "Kubwa, FCT"; a city named like its state ("Lagos") is not repeated. */
export const cityLabel = (city: City): string => (city.name === city.state ? city.name : `${city.name}, ${city.state}`)

/** Great-circle distance in kilometres. */
export const distanceKm = (aLat: number, aLng: number, bLat: number, bLng: number): number => {
  const R = 6371.0088
  const toRad = (deg: number) => (deg * Math.PI) / 180
  const dLat = toRad(bLat - aLat)
  const dLng = toRad(bLng - aLng)
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(aLat)) * Math.cos(toRad(bLat)) * Math.sin(dLng / 2) ** 2
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)))
}

/** Kilometres between two catalogue cities. */
export const cityDistanceKm = (a: City, b: City): number => distanceKm(a.latitude, a.longitude, b.latitude, b.longitude)

/**
 * "· about 12 km" for a kitchen in another city; nothing inside the
 * customer's own city, where a city-centre figure would only mislead.
 */
export const distanceNote = (km: number | undefined): string =>
  typeof km === 'number' && km >= 3 ? ` · about ${Math.round(km)} km` : ''

/** The nearest catalogue city to a fix, or undefined beyond maxKm. */
export const nearestCityTo = (cities: readonly City[], lat: number, lng: number, maxKm = 60): City | undefined => {
  if (!Number.isFinite(lat) || !Number.isFinite(lng) || Math.abs(lat) > 90 || Math.abs(lng) > 180) return undefined
  let best: City | undefined
  let bestKm = Infinity
  for (const city of cities) {
    const km = distanceKm(lat, lng, city.latitude, city.longitude)
    if (km < bestKm) {
      best = city
      bestKm = km
    }
  }
  return bestKm <= maxKm ? best : undefined
}

let catalogue: readonly City[] | undefined
let pending: Promise<readonly City[]> | undefined

/** Load the catalogue once per page. It changes only with a deploy. */
export const loadCities = (): Promise<readonly City[]> => {
  if (catalogue) return Promise.resolve(catalogue)
  pending ??= fetch(`${API_BASE_URL}/v1/discover/cities`, {
    headers: { Accept: 'application/json' },
    signal: AbortSignal.timeout(8000),
  })
    .then(async (resp) => {
      if (!resp.ok) throw new Error(`City catalogue read failed with status ${resp.status}`)
      const body = (await resp.json()) as { cities?: unknown }
      const list = Array.isArray(body.cities) ? (body.cities as City[]) : []
      catalogue = list
      return list
    })
    .finally(() => {
      // A failed read is retried by the next caller rather than cached.
      pending = undefined
    })
  return pending
}

/** The catalogue if it has already loaded, else empty. Never fetches. */
export const loadedCities = (): readonly City[] => catalogue ?? []

export interface CityCatalogue {
  cities: readonly City[]
  byRef: ReadonlyMap<string, City>
  loading: boolean
  error: boolean
}

/** The catalogue for pickers and distance labels. Empty until it loads. */
export const useCities = (): CityCatalogue => {
  const [cities, setCities] = useState<readonly City[]>(() => catalogue ?? [])
  const [loading, setLoading] = useState(() => catalogue === undefined)
  const [error, setError] = useState(false)
  useEffect(() => {
    if (catalogue) return
    let active = true
    void loadCities()
      .then((list) => {
        if (active) setCities(list)
      })
      .catch(() => {
        if (active) setError(true)
      })
      .finally(() => {
        if (active) setLoading(false)
      })
    return () => {
      active = false
    }
  }, [])
  const byRef = useMemo(() => new Map(cities.map((city) => [city.ref, city])), [cities])
  return { cities, byRef, loading, error }
}

/** Test seam: forget the loaded catalogue. */
export const resetCityCatalogueForTests = (): void => {
  catalogue = undefined
  pending = undefined
}
